import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  type ReactNode,
} from 'react';

/**
 * What the sticky header's save bar shows, published by whichever page is open.
 *
 * The bar and the form it saves live in different components — the design puts
 * the bar in the shell's header and the fields in the page below it — so one of
 * them has to reach the other. A context rather than moving the bar into the
 * page, because the header is sticky and shared by every editor that will
 * follow this one.
 */
export interface EditorBarState {
  /** Whether there is anything to save. Absent means there is no form here. */
  dirty: boolean;
  saving: boolean;
  /** What went wrong with the last attempt, if anything. */
  error: string | null;
  save: () => void;
}

interface EditorBarContext {
  bar: EditorBarState | null;
  publish: (state: EditorBarState | null) => void;
}

const Context = createContext<EditorBarContext | null>(null);

export function EditorBarProvider({ children }: { children: ReactNode }) {
  const [bar, setBar] = useState<EditorBarState | null>(null);

  /*
   * Replaced only when something actually differs. The page publishes from an
   * effect that runs on every render, so storing the object unconditionally
   * would set state on every render and loop. `save` is compared by identity,
   * which is why the page has to hand over a stable callback.
   */
  const publish = useCallback((next: EditorBarState | null) => {
    setBar((previous) => {
      if (previous === next) return previous;
      if (!previous || !next) return next;
      const same =
        previous.dirty === next.dirty &&
        previous.saving === next.saving &&
        previous.error === next.error &&
        previous.save === next.save;
      return same ? previous : next;
    });
  }, []);

  const value = useMemo(() => ({ bar, publish }), [bar, publish]);
  return <Context.Provider value={value}>{children}</Context.Provider>;
}

export const useEditorBar = (): EditorBarContext => {
  const value = useContext(Context);
  if (!value) throw new Error('useEditorBar used outside EditorBarProvider.');
  return value;
};

/**
 * What a page puts in the header beside the save bar.
 *
 * The design moves "New app", "Topics" and "New post" out of the page and into
 * the sticky header. Those buttons open things the page owns — a modal, a
 * draft — so the shell cannot own them: it would have to know which page is
 * open and what each one's dialog is called. The page keeps its own state and
 * hands the header a list of buttons, exactly the way an editor hands it a
 * save bar.
 */
export interface PageAction {
  label: string;
  onClick: () => void;
  /** `accent` is the one thing the page is for; the rest are outlined. */
  kind?: 'accent' | 'outline';
  /**
   * A mark before the label, drawn by the header and hidden from assistive
   * technology.
   *
   * The design writes these buttons as "＋ New post". Putting that character in
   * the label would make the button's accessible name "＋ New post" too, which
   * a screen reader reads out as the character's name before the words — the
   * same mistake the icons in `icons.tsx` exist to avoid.
   */
  icon?: 'plus';
}

/**
 * One button in a segmented control the page publishes into the header.
 *
 * The Overview's date range lives there in v2, beside the profile button rather
 * than above the numbers it filters. Separate from `PageAction` because a
 * segment is a choice among several and a button is a thing to press — they
 * look different, are grouped differently, and only one of them has a selected
 * state.
 */
export interface PageSegment {
  label: string;
  onClick: () => void;
  pressed: boolean;
}

interface PageActionsContext {
  actions: PageAction[];
  publishActions: (next: PageAction[]) => void;
  segments: PageSegment[];
  publishSegments: (next: PageSegment[]) => void;
}

const ActionsContext = createContext<PageActionsContext | null>(null);

export function PageActionsProvider({ children }: { children: ReactNode }) {
  const [actions, setActions] = useState<PageAction[]>([]);
  const [segments, setSegments] = useState<PageSegment[]>([]);

  /*
   * Replaced only when something differs, for the reason `publish` above gives:
   * the page publishes from an effect that runs on every render, so storing the
   * array unconditionally sets state on every render and loops. Compared field
   * by field rather than by identity, so a page may build the array inline.
   */
  const publishActions = useCallback((next: PageAction[]) => {
    setActions((previous) => {
      const same =
        previous.length === next.length &&
        previous.every(
          (one, at) =>
            one.label === next[at].label &&
            one.onClick === next[at].onClick &&
            one.kind === next[at].kind &&
            one.icon === next[at].icon,
        );
      return same ? previous : next;
    });
  }, []);

  const publishSegments = useCallback((next: PageSegment[]) => {
    setSegments((previous) => {
      const same =
        previous.length === next.length &&
        previous.every(
          (one, at) =>
            one.label === next[at].label &&
            one.onClick === next[at].onClick &&
            one.pressed === next[at].pressed,
        );
      return same ? previous : next;
    });
  }, []);

  const value = useMemo(
    () => ({ actions, publishActions, segments, publishSegments }),
    [actions, publishActions, segments, publishSegments],
  );
  return <ActionsContext.Provider value={value}>{children}</ActionsContext.Provider>;
}

export const usePageActions = (): PageActionsContext => {
  const value = useContext(ActionsContext);
  if (!value) throw new Error('usePageActions used outside PageActionsProvider.');
  return value;
};

/**
 * Publishes this page's header buttons for as long as it is open.
 *
 * `actions` must be memoised by the caller — an array built fresh on every
 * render would be a new value every time, which the comparison above forgives
 * but the effect below would still re-run.
 */
export const useHeaderActions = (actions: PageAction[]): void => {
  const { publishActions } = usePageActions();

  useEffect(() => {
    publishActions(actions);
    // Cleared on the way out, so the next page does not inherit them.
    return () => publishActions([]);
  }, [actions, publishActions]);
};

/** Publishes this page's segmented control for as long as it is open. */
export const useHeaderSegments = (segments: PageSegment[]): void => {
  const { publishSegments } = usePageActions();

  useEffect(() => {
    publishSegments(segments);
    return () => publishSegments([]);
  }, [segments, publishSegments]);
};
